import React from "react";
import {
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  Box,
  Alert,
  Chip,
} from "@mui/material";
import { useNavigate } from "react-router-dom";

// Icons
import SchoolIcon from "@mui/icons-material/School";
import PeopleIcon from "@mui/icons-material/People";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import SearchIcon from "@mui/icons-material/Search";
import ScienceIcon from "@mui/icons-material/Science";
import PsychologyIcon from "@mui/icons-material/Psychology";
import TimelineIcon from "@mui/icons-material/Timeline";

// Epic Health Records Connect
import ConnectEpicButton from "../components/ConnectEpicButton";

const Dashboard = () => {
  const navigate = useNavigate();

  // TODO: Get this from user context/auth
  const isPremium = false; // Change to true for premium users

  const features = [
    {
      title: "Amyloidosis Education",
      description:
        "Learn about different types of amyloidosis, symptoms, and treatments",
      icon: <SchoolIcon sx={{ fontSize: 70 }} />,
      color: "#1A5EA8",
      path: "/amyloid",
    },
    {
      title: "Patient Management",
      description:
        "Comprehensive tools for managing patient information and medical history",
      icon: <PeopleIcon sx={{ fontSize: 70 }} />,
      color: "#f093fb",
      path: "/patients",
    },
    {
      title: "Medical Glossary",
      description: "200+ medical terms with AI-powered voice pronunciations",
      icon: <VolumeUpIcon sx={{ fontSize: 70 }} />,
      color: "#4facfe",
      path: "/tools/glossary",
      badge: "🎧 Audio",
      featured: true,
    },
    {
      title: "Clinical Trial Search",
      description: "Search and compare clinical trials from multiple databases",
      icon: <SearchIcon sx={{ fontSize: 70 }} />,
      color: "#43e97b",
      path: "/amyloid/clinicaltrialsearch",
    },
    {
      title: "Trial Management",
      description: "Create and manage clinical trials in our database",
      icon: <ScienceIcon sx={{ fontSize: 70 }} />,
      color: "#fa709a",
      path: "/admin/trials/new",
    },
    {
      title: "Patient-Trial Matching",
      description:
        "Advanced AI-powered matching algorithm for optimal trial selection",
      icon: <PsychologyIcon sx={{ fontSize: 70 }} />,
      color: "#0B2545",
      path: "/trials/patientmatching",
    },
    {
      title: "Longitudinal Tracking",
      description: "Track patient progress and outcomes over time",
      icon: <TimelineIcon sx={{ fontSize: 70 }} />,
      color: "#11998e",
      path: "/patients",
    },
  ];

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 5, textAlign: "center" }}>
        <Typography
          variant="h3"
          component="h1"
          gutterBottom
          sx={{
            fontWeight: "bold",
            background: "linear-gradient(135deg, #1A5EA8 0%, #0B2545 100%)",
            backgroundClip: "text",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
          }}
        >
          Welcome to NephroTrax
        </Typography>
        <Typography variant="h6" color="text.secondary">
          Your comprehensive platform for amyloidosis clinical trials and
          education
        </Typography>

        {/* Connect Epic Health Records */}
        <Box sx={{ mt: 3, display: "flex", justifyContent: "center" }}>
          <ConnectEpicButton />
        </Box>
      </Box>

      {/* Feature Cards */}
      <Grid container spacing={4}>
        {features.map((feature, index) => (
          <Grid item xs={12} md={6} key={index}>
            <Card
              sx={{
                height: 320,
                maxWidth: 500,
                mx: "auto",
                display: "flex",
                flexDirection: "column",
                cursor: "pointer",
                transition: "all 0.3s ease",
                position: "relative",
                border: feature.featured ? "3px solid" : "2px solid",
                borderColor: feature.featured ? feature.color : "grey.300",
                boxShadow: feature.featured ? 4 : 2,
                "&:hover": {
                  transform: "translateY(-8px)",
                  boxShadow: 8,
                },
              }}
              onClick={() => navigate(feature.path)}
            >
              {/* Featured Badge */}
              {feature.featured && (
                <Box
                  sx={{
                    position: "absolute",
                    top: 10,
                    right: 10,
                    bgcolor: feature.color,
                    color: "white",
                    px: 2,
                    py: 0.5,
                    borderRadius: 2,
                    fontSize: "0.75rem",
                    fontWeight: "bold",
                    boxShadow: 2,
                    zIndex: 1,
                    animation: "pulse 2s infinite",
                    "@keyframes pulse": {
                      "0%, 100%": {
                        opacity: 1,
                      },
                      "50%": {
                        opacity: 0.8,
                      },
                    },
                  }}
                >
                  NEW FEATURE
                </Box>
              )}

              {/* Audio Badge */}
              {feature.badge && (
                <Box
                  sx={{
                    position: "absolute",
                    top: 45,
                    right: 10,
                    bgcolor: "rgba(255,255,255,0.95)",
                    color: feature.color,
                    px: 1.5,
                    py: 0.5,
                    borderRadius: 1.5,
                    fontSize: "0.7rem",
                    fontWeight: 600,
                    border: `2px solid ${feature.color}`,
                    zIndex: 1,
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                    boxShadow: 1,
                  }}
                >
                  {feature.badge}
                </Box>
              )}

              <CardContent sx={{ flexGrow: 1, textAlign: "center", pt: 4 }}>
                <Box
                  sx={{
                    mb: 2,
                    display: "flex",
                    justifyContent: "center",
                  }}
                >
                  <Box
                    sx={{
                      width: 70,
                      height: 70,
                      borderRadius: "50%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      background: `linear-gradient(135deg, ${feature.color}22 0%, ${feature.color}44 100%)`,
                      color: feature.color,
                    }}
                  >
                    {feature.icon}
                  </Box>
                </Box>
                <Typography
                  variant="h5"
                  component="h3"
                  fontWeight="bold"
                  gutterBottom
                  sx={{
                    minHeight: 65,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  {feature.title}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {feature.description}
                </Typography>
              </CardContent>
              <CardActions sx={{ justifyContent: "center", pb: 3 }}>
                <Button
                  variant="contained"
                  size="large"
                  sx={{
                    background: `linear-gradient(135deg, ${feature.color} 0%, ${feature.color}dd 100%)`,
                    py: 1.5,
                    px: 4,
                    fontWeight: "bold",
                    "&:hover": {
                      background: `linear-gradient(135deg, ${feature.color}dd 0%, ${feature.color}bb 100%)`,
                    },
                  }}
                >
                  Explore
                </Button>
              </CardActions>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Premium users - Thank you message */}
      {isPremium && (
        <Box sx={{ mt: 6, textAlign: "center" }}>
          <Alert severity="success" sx={{ display: "inline-flex" }}>
            <strong>💎 Thank you for being a Premium member!</strong> Enjoying
            full access to all features.
          </Alert>
        </Box>
      )}
    </Container>
  );
};

export default Dashboard;